import type { RefObject } from 'react';
import type React from 'react';
import { forwardRef } from 'react';

import { tailwindMerge } from '../../tailwind';
import type { IconProps } from '../Icons';
import { BottomArrowSolidIcon, SpinnerAnimatedSolidIcon } from '../Icons';
import { ButtonLabel } from '../theme';

type ButtonType = 'button' | 'submit' | 'reset';
type Semantic = 'danger' | 'primary' | 'warning';

const BUTTON_ICON_SIZE = 16;
export interface ButtonProps
  extends Omit<React.HTMLProps<HTMLButtonElement>, 'css' | 'as'> {
  semantic?: Semantic;
  variant?: 'primary' | 'secondary' | 'tertiary';
  isLoading?: boolean;
  OutlinedIcon?: React.ComponentType<IconProps>;
  SolidIcon?: React.ComponentType<IconProps>;
  IllustrationIcon?: React.ComponentType<IconProps>;
  selectIcon?: boolean;
  iconSize?: number;
  testId?: string;
  fullWidth?: boolean;
  iconClassName?: string;

  // Overwritten props
  ref?: RefObject<HTMLButtonElement>;
  type?: ButtonType;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      semantic = 'primary',
      variant = 'primary',
      type = 'button',
      isLoading,
      OutlinedIcon,
      SolidIcon,
      IllustrationIcon,
      selectIcon,
      children,
      testId,
      className,
      fullWidth,
      iconClassName,
      iconSize = BUTTON_ICON_SIZE,
      ...props
    }: ButtonProps,
    ref,
  ) => {
    const disabled = props.disabled ?? isLoading;
    const isActionable = !disabled && !isLoading;
    return (
      <button
        ref={ref}
        type={type}
        data-testid={testId}
        className={tailwindMerge(
          'group/button m-zero h-button border-zero px-m py-s relative inline-block cursor-pointer appearance-none rounded text-center align-middle whitespace-nowrap text-white no-underline shadow-[0_0_0_2px] shadow-transparent outline-0 select-none print:opacity-100',
          backgroundColorClassBySemanticForPrimaryVariant[semantic],
          isActionable &&
            `duration-short ease-accelerate transition-[border,transform,box-shadow] active:scale-[0.98] ${interactiveClassesBySemanticForPrimaryVariant[semantic]}`,
          variant !== 'primary' &&
            `bg-inherit ${textColorClassBySemanticForNotPrimaryVariant[semantic]}`,
          variant === 'secondary' &&
            `border border-solid ${borderColorClassBySemanticForSecondaryVariant[semantic].base} py-[calc(theme(spacing.s)_-_theme(borderWidth.regular))]`,
          variant === 'secondary' &&
            isActionable &&
            `hover:bg-inherit focus:bg-inherit ${borderColorClassBySemanticForSecondaryVariant[semantic].focus} ${borderColorClassBySemanticForSecondaryVariant[semantic].hover}`,
          variant === 'tertiary' &&
            isActionable &&
            `hover:shadow-none ${hoverAndFocusBackgroundColorClassesBySemanticForTertiaryVariant[semantic]}`,
          OutlinedIcon || SolidIcon ? 'min-w-button-with-icon' : 'min-w-button',
          fullWidth && 'w-full',
          disabled && 'cursor-not-allowed opacity-60',
          isLoading && 'cursor-default',
          className,
        )}
        {...props}
        disabled={disabled}
      >
        {isLoading && (
          <SpinnerAnimatedSolidIcon
            size={24}
            className={tailwindMerge(
              'absolute left-[calc(50%-12px)]',
              variant === 'primary'
                ? 'fill-white'
                : solidIconColorClassBySemanticForNotPrimaryVariant[semantic],
            )}
            data-testid={`loader-${testId}`}
          />
        )}
        <div
          className={`gap-s flex items-center justify-center ${
            isLoading ? 'invisible' : ''
          }`}
        >
          {OutlinedIcon && (
            <OutlinedIcon
              size={iconSize}
              className={tailwindMerge(
                disabled && 'opacity-60',
                variant === 'primary'
                  ? 'stroke-white'
                  : outlinedIconColorClassBySemanticForNotPrimaryVariant[
                      semantic
                    ],
                variant !== 'primary' &&
                  !disabled &&
                  outlinedIconHoverAndFocusClassBySemanticForNotPrimaryVariant[
                    semantic
                  ],
                iconClassName,
              )}
            />
          )}
          {SolidIcon && (
            <SolidIcon
              size={iconSize}
              className={tailwindMerge(
                disabled && 'opacity-60',
                variant === 'primary'
                  ? 'fill-white'
                  : solidIconColorClassBySemanticForNotPrimaryVariant[semantic],
                variant !== 'primary' &&
                  !disabled &&
                  solidIconHoverAndFocusClassBySemanticForNotPrimaryVariant[
                    semantic
                  ],
                iconClassName,
              )}
            />
          )}
          {IllustrationIcon && <IllustrationIcon size={iconSize} />}
          {children && (
            <ButtonLabel className={tailwindMerge(disabled && 'opacity-60')}>
              {children}
            </ButtonLabel>
          )}
          {selectIcon && (
            <BottomArrowSolidIcon
              className={tailwindMerge(
                disabled && 'opacity-60',
                variant === 'primary'
                  ? 'fill-white'
                  : solidIconColorClassBySemanticForNotPrimaryVariant[semantic],
                variant !== 'primary' &&
                  !disabled &&
                  solidIconHoverAndFocusClassBySemanticForNotPrimaryVariant[
                    semantic
                  ],
              )}
              size={8}
            />
          )}
        </div>
      </button>
    );
  },
);

const backgroundColorClassBySemanticForPrimaryVariant: Record<
  Semantic,
  string
> = {
  primary: 'bg-components-primary-base',
  warning: 'bg-components-warning-base',
  danger: 'bg-components-danger-base',
};

const interactiveClassesBySemanticForPrimaryVariant: Record<Semantic, string> =
  {
    primary:
      'hover:bg-components-primary-hover hover:shadow-components-primary-outline focus:bg-components-primary-focus focus:shadow-components-primary-outline active:animate-button-focus-primary',
    warning:
      'hover:bg-components-warning-hover hover:shadow-components-warning-outline focus:bg-components-warning-focus focus:shadow-components-warning-outline active:animate-button-focus-warning',
    danger:
      'hover:bg-components-danger-hover hover:shadow-components-danger-outline focus:bg-components-danger-focus focus:shadow-components-danger-outline active:animate-button-focus-danger',
  };

const textColorClassBySemanticForNotPrimaryVariant: Record<Semantic, string> = {
  primary:
    'text-components-primary-base hover:text-components-primary-hover focus:text-components-primary-focus',
  warning:
    'text-components-warning-base hover:text-components-warning-hover focus:text-components-warning-focus',
  danger:
    'text-components-danger-base hover:text-components-danger-hover focus:text-components-danger-focus',
};

const borderColorClassBySemanticForSecondaryVariant: Record<
  Semantic,
  { base: string; hover: string; focus: string }
> = {
  primary: {
    base: 'border-components-primary-base',
    hover: 'hover:border-components-primary-hover',
    focus: 'focus:border-components-primary-focus',
  },
  warning: {
    base: 'border-components-warning-base',
    hover: 'hover:border-components-warning-hover',
    focus: 'focus:border-components-warning-focus',
  },
  danger: {
    base: 'border-components-danger-base',
    hover: 'hover:border-components-danger-hover',
    focus: 'focus:border-components-danger-focus',
  },
};

const hoverAndFocusBackgroundColorClassesBySemanticForTertiaryVariant: Record<
  Semantic,
  string
> = {
  primary:
    'hover:bg-components-primary-background focus:bg-components-primary-background',
  warning:
    'hover:bg-components-warning-background focus:bg-components-warning-background',
  danger:
    'hover:bg-components-danger-background focus:bg-components-danger-background',
};

const solidIconColorClassBySemanticForNotPrimaryVariant: Record<
  Semantic,
  string
> = {
  primary: 'fill-components-primary-base',
  warning: 'fill-components-warning-base',
  danger: 'fill-components-danger-base',
};

const outlinedIconColorClassBySemanticForNotPrimaryVariant: Record<
  Semantic,
  string
> = {
  primary: 'stroke-components-primary-base',
  warning: 'stroke-components-warning-base',
  danger: 'stroke-components-danger-base',
};

const outlinedIconHoverAndFocusClassBySemanticForNotPrimaryVariant: Record<
  Semantic,
  string
> = {
  primary:
    'group-hover/button:stroke-components-primary-hover group-focus/button:stroke-components-primary-focus',
  warning:
    'group-hover/button:stroke-components-warning-hover group-focus/button:stroke-components-warning-focus',
  danger:
    'group-hover/button:stroke-components-danger-hover group-focus/button:stroke-components-danger-focus',
};

const solidIconHoverAndFocusClassBySemanticForNotPrimaryVariant: Record<
  Semantic,
  string
> = {
  primary:
    'group-hover/button:fill-components-primary-hover group-focus/button:fill-components-primary-focus',
  warning:
    'group-hover/button:fill-components-warning-hover group-focus/button:fill-components-warning-focus',
  danger:
    'group-hover/button:fill-components-danger-hover group-focus/button:fill-components-danger-focus',
};
